/**
 * Apple-Grade Procedural Audio Engine
 * Uses warm harmonic overtones, polyphonic frequency envelopes,
 * and low-pass acoustic filtering for luxurious AirDrop/Handoff chimes.
 */

class SoundEngine {
  private ctx: AudioContext | null = null;
  private enabled: boolean = true;

  constructor() {
    // AudioContext will be initialized on first user gesture / event
  }

  private getContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  public setEnabled(val: boolean) {
    this.enabled = val;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Apple AirDrop / iOS Handoff Style Crystal Sync Chime
   * Warm harmonic major-triad chime (C6 -> E6 -> G6) with soft glass decay.
   */
  public playSyncSuccess() {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Master bus with warm analog-style lowpass filter
    const masterGain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(3200, now);
    filter.Q.setValueAtTime(0.7, now);

    masterGain.gain.setValueAtTime(0.18, now);
    masterGain.connect(filter);
    filter.connect(ctx.destination);

    // Harmonic triad chords: C6 (1046.5Hz), E6 (1318.5Hz), G6 (1567.98Hz)
    const tones = [
      { freq: 1046.5, time: 0, duration: 0.28, gain: 0.7 },
      { freq: 1318.5, time: 0.04, duration: 0.32, gain: 0.85 },
      { freq: 1567.98, time: 0.08, duration: 0.45, gain: 1.0 },
      { freq: 2093.0, time: 0.12, duration: 0.55, gain: 0.6 } // High shimmer
    ];

    tones.forEach(({ freq, time, duration, gain }) => {
      const osc = ctx.createOscillator();
      const noteGain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq * 0.98, now + time);
      osc.frequency.exponentialRampToValueAtTime(freq, now + time + 0.03);

      noteGain.gain.setValueAtTime(0.001, now + time);
      noteGain.gain.exponentialRampToValueAtTime(gain * 0.4, now + time + 0.015);
      noteGain.gain.exponentialRampToValueAtTime(0.0001, now + time + duration);

      osc.connect(noteGain);
      noteGain.connect(masterGain);

      osc.start(now + time);
      osc.stop(now + time + duration + 0.05);
    });
  }

  /**
   * Apple Handoff / Device Connected Soft Bubble Tone
   */
  public playDeviceJoined() {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2400, now);

    masterGain.gain.setValueAtTime(0.14, now);
    masterGain.connect(filter);
    filter.connect(ctx.destination);

    // Double bubble tap: G5 (783.99Hz) -> C6 (1046.5Hz)
    const notes = [
      { freq: 783.99, time: 0, duration: 0.18 },
      { freq: 1046.5, time: 0.07, duration: 0.35 }
    ];

    notes.forEach(({ freq, time, duration }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq * 0.95, now + time);
      osc.frequency.exponentialRampToValueAtTime(freq, now + time + 0.02);

      g.gain.setValueAtTime(0.001, now + time);
      g.gain.exponentialRampToValueAtTime(0.5, now + time + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + time + duration);

      osc.connect(g);
      g.connect(masterGain);

      osc.start(now + time);
      osc.stop(now + time + duration + 0.02);
    });
  }

  /**
   * Soft Muted Haptic Thud (Gentle error notification)
   */
  public playError() {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(400, now);

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(110, now + 0.18);

    g.gain.setValueAtTime(0.12, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

    osc.connect(filter);
    filter.connect(g);
    g.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.2);
  }

  public playCopy() {
    this.playSyncSuccess();
  }

  public playPush() {
    this.playDeviceJoined();
  }
}

export const sound = new SoundEngine();
